import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, LogOut } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { buildExpandedAccess } from "~/pages/manifold-nextgen/components/share/AccessDetailsView";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import { nextgenProjectsListPath } from "~/pages/manifold-nextgen/paths";
import { deleteProject, leaveProject, projectListPath } from "~/services/projects";
import { DeleteProjectDialog } from "./DeleteProjectDialog";
import { LeaveProjectDialog } from "./LeaveProjectDialog";
import { useProjectContext } from "./useProjectContext";

export const ProjectSettings = () => {
  const { projectId, project, isOwner } = useProjectContext();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { collaborators, accessLevels } = useProjectShare(projectId);
  const memberCount = collaborators
    ? buildExpandedAccess(collaborators.users, collaborators.groups, accessLevels).length
    : undefined;

  const handleDelete = useCallback(async () => {
    try {
      await deleteProject(projectId);
      await mutate(projectListPath());
      showSuccess(`"${project.name}" archived`);
      navigate(nextgenProjectsListPath());
    } catch {
      showError("Failed to archive project. Please try again.");
    }
  }, [projectId, project.name, navigate, showSuccess, showError]);

  // Throws on failure so LeaveProjectDialog can surface the reason inline (e.g.
  // the "keep at least one owner" 409 when the caller is the only owner).
  const handleLeave = useCallback(async () => {
    await leaveProject(projectId);
    await mutate(projectListPath());
    navigate(nextgenProjectsListPath());
  }, [projectId, navigate]);

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        <SettingsSection title="Danger Zone" danger>
          <SettingsRow
            title="Leave project"
            description="You'll lose access to this project unless it's shared with you again."
          >
            <Button variant="outline" className="w-32" onClick={() => setLeaveOpen(true)}>
              <LogOut className="size-4" />
              Leave
            </Button>
          </SettingsRow>
          {isOwner && (
            <SettingsRow
              title="Archive project"
              description="Archive and hide this project. An admin can restore if necessary."
            >
              <Button variant="destructive" className="w-32" onClick={() => setDeleteOpen(true)}>
                <Archive className="size-4" />
                Archive
              </Button>
            </SettingsRow>
          )}
        </SettingsSection>
      </div>

      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        projectName={project.name}
        memberCount={memberCount}
        onConfirm={handleDelete}
      />
      <LeaveProjectDialog
        open={leaveOpen}
        onOpenChange={setLeaveOpen}
        projectName={project.name}
        onConfirm={handleLeave}
      />
    </div>
  );
};
